import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, RotateCw } from "lucide-react";
import { AuthLayout } from "@/components/AuthLayout";
import { AUTH } from "@/constants/testIds";
import { api, formatApiError } from "@/lib/api";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function OTPVerificationPage() {
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const purpose = params.get("purpose") || "email_verify";
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    if (!email) {
      setError("Missing email. Please restart the verification.");
      return;
    }
    if (otp.length < 6) {
      setError("Please enter the full 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/verify-otp", { email, otp_code: otp, purpose });
      toast.success("Email verified!");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/send-otp", { email, purpose });
      toast.success("A new code is on the way.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout
      title="Verify your email"
      subtitle={email ? `We sent a 6-digit code to ${email}.` : "We sent a 6-digit code to your inbox."}
      hint={<Link to="/login" className="font-semibold text-[#101876] hover:underline">Back to login</Link>}
    >
      <form onSubmit={verify} className="space-y-5" data-testid={AUTH.otpRoot}>
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        <div className="flex justify-center" data-testid={AUTH.otpInput}>
          <InputOTP maxLength={6} value={otp} onChange={setOtp}>
            <InputOTPGroup>
              {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}
            </InputOTPGroup>
          </InputOTP>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#101876] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0c135c] disabled:opacity-70"
          data-testid={AUTH.otpSubmit}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          {loading ? "Verifying…" : "Verify code"}
        </button>
        <button
          type="button"
          onClick={resend}
          disabled={resending}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876] disabled:opacity-70"
          data-testid={AUTH.otpResend}
        >
          {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}
          Resend code
        </button>
      </form>
    </AuthLayout>
  );
}
